import { provide, inject, type InjectionKey } from 'vue'
import type { PostHog, PostHogConfig } from 'posthog-js'

const PostHogSymbol: InjectionKey<PostHog> = Symbol('PostHog')

const noop = () => {}

const posthogStub = {
  capture: noop,
  identify: noop,
  alias: noop,
  group: noop,
  setPersonProperties: noop,
  reset: noop,
  register: noop,
  unregister: noop,
  reloadFeatureFlags: noop,
  isFeatureEnabled: () => false,
  getFeatureFlag: () => undefined,
  getFeatureFlagPayload: () => undefined
} as unknown as PostHog

/**
 * Provides PostHog instance to the Vue application
 * @param apiKey - PostHog project API key
 * @param config - PostHog configuration options
 * @returns PostHog instance
 */
export function usePostHogProvider(
  _apiKey: string,
  _config: Partial<PostHogConfig> = {}
): PostHog {
  provide<PostHog>(PostHogSymbol, posthogStub)
  return posthogStub
}

/**
 * Injects PostHog instance into a component
 * @throws Error if PostHog instance is not provided
 * @returns PostHog instance
 */
export function usePostHog(): PostHog {
  const posthogInstance = inject<PostHog>(PostHogSymbol, null as unknown as PostHog)

  if (!posthogInstance) {
    throw new Error(
      'PostHog has not been provided. Make sure to call usePostHogProvider first.'
    )
  }

  return posthogInstance
}
